import {
  applyStatePrecision,
  FLOATING_POINT_PRECISION,
} from "encamp-shared/src/units/amountPrecision";
import { convert } from "encamp-shared/src/units/unitConversions";
import { UnitType } from "generated-graphql/graphql";

import { toGraphqlUnitType, toPrismaDensityUnits, toPrismaUnitType } from "./reportingUnitEnums";

export type DisplayAmountsInput = {
  maxAmount: number | null | undefined;
  /** The unit the amount above is actually expressed in. */
  unit: string | null | undefined;
  density: number | null | undefined;
  densityUnits: string | null | undefined;
  /**
   * The unit staff have picked in the form but not yet saved. `null` or
   * `undefined` means nothing is selected, so there is nothing to preview.
   */
  targetUnit: UnitType | null | undefined;
  /**
   * The facility's state, so the previewed number is normalised exactly as the
   * persisted one will be. Without it, CA and KS staff read more digits here
   * than end up on the filed row.
   */
  state: string | null | undefined;
};

export type DisplayAmounts = {
  /** Unit the returned amount is expressed in. */
  unit: UnitType | null;
  maxAmount: number | null;
  /** A target unit is selected and the conversion into it succeeded. */
  isOverridden: boolean;
  /** A target unit is selected but converting into it needs a density the chemical lacks. */
  densityMissing: boolean;
};

function passthrough(input: DisplayAmountsInput, densityMissing: boolean): DisplayAmounts {
  return {
    unit: toGraphqlUnitType(input.unit),
    maxAmount: input.maxAmount ?? null,
    isOverridden: false,
    densityMissing,
  };
}

/**
 * Live preview of what the reporting-unit override form block would convert
 * the max amount to under the unit staff have selected but not yet saved.
 *
 * This is genuinely client-side: nobody has submitted the selection, so
 * aggregation has nothing to convert yet. Once an override is saved,
 * aggregation applies it and persists the reported figures directly onto the
 * RFC — the Summary Card reads those (and the `preOverride*` originals)
 * straight from the server rather than calling this helper.
 */
export function resolveDisplayAmounts(input: DisplayAmountsInput): DisplayAmounts {
  const { targetUnit } = input;
  if (!targetUnit) {
    return passthrough(input, false);
  }

  const fromUnit = toPrismaUnitType(input.unit);
  const toUnit = toPrismaUnitType(targetUnit);
  if (!fromUnit || !toUnit || input.maxAmount == null) {
    return passthrough(input, false);
  }

  const density = input.density ?? null;
  const densityUnits = toPrismaDensityUnits(input.densityUnits);

  try {
    const converted = convert(input.maxAmount, fromUnit, toUnit, density, densityUnits);
    return {
      unit: targetUnit,
      // Normalised the same way applyReportingUnitOverride normalises what it
      // persists, so the figure staff read before saving is the figure that
      // gets filed. Skipping this let CA and KS previews carry digits the
      // stored amount does not.
      maxAmount:
        converted == null
          ? converted
          : applyStatePrecision(
              Number(converted.toPrecision(FLOATING_POINT_PRECISION)),
              "maxAmount",
              input.state
            ),
      isOverridden: true,
      densityMissing: false,
    };
  } catch {
    // `convert` throws MissingDensity for an unconvertible mass<->volume
    // change and UnsupportedUnitConversion for pairs it cannot handle at all.
    // Either way the preview has nothing honest to show in the target unit,
    // so fall back to the source value and let the caller render the warning.
    return passthrough(input, true);
  }
}
